import { cn } from "@/utils/cn";

/**
 * Identical 32×32 circle for page / prev / next so active never looks bigger than neighbors.
 */
const CONTROL_BOX = [
  "inline-flex items-center justify-center shrink-0 box-border",
  "!h-8 !w-8 !min-h-8 !min-w-8 !max-h-8 !max-w-8 !p-0 !m-0",
  "!rounded-full leading-none",
  "transition-colors",
  "focus:outline-none focus-visible:ring-1 focus-visible:ring-purple/40",
  "disabled:opacity-40 disabled:cursor-not-allowed",
].join(" ");

/** Root `<nav>` wrapping the pagination region. */
export function getPaginationNavClassName(className?: string): string {
  return cn("flex max-w-full flex-wrap items-center", className);
}

/** `<ul>` listing page controls — wrap on narrow viewports instead of scrolling. */
export function getPaginationContentClassName(className?: string): string {
  return cn("flex flex-wrap items-center gap-1", className);
}

/**
 * Page number button.
 * Active hover must keep dark fill + light text (Button icon hover would wash it out).
 * Inactive hover: soft fill only.
 */
export function getPaginationLinkClassName(
  isActive?: boolean,
  className?: string
): string {
  return cn(
    CONTROL_BOX,
    "border text-sm font-medium font-body tabular-nums",
    isActive
      ? [
          "!border-dark-invirtus !bg-dark-invirtus !text-light",
          "hover:!border-dark-invirtus hover:!bg-dark-invirtus/90 hover:!text-light",
          "focus-visible:!bg-dark-invirtus focus-visible:!text-light",
        ].join(" ")
      : [
          "!border-transparent !bg-transparent text-dark-invirtus/60",
          "hover:!border-transparent hover:!bg-dark-invirtus-soft hover:!text-dark-invirtus",
        ].join(" "),
    className
  );
}

/** Previous / Next — same circle box as page numbers. */
export function getPaginationPrevNextClassName(className?: string): string {
  return cn(
    CONTROL_BOX,
    "!border !border-light-lila/60 !bg-transparent text-dark-invirtus/60",
    "hover:!bg-dark-invirtus-soft/50 hover:!text-dark-invirtus",
    "[&_svg]:!h-4 [&_svg]:!w-4 [&_svg]:shrink-0",
    className
  );
}

export function getPaginationEllipsisClassName(className?: string): string {
  return cn(
    CONTROL_BOX,
    "!border-transparent text-dark-invirtus/40",
    "[&_svg]:!h-4 [&_svg]:!w-4",
    className
  );
}
